import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store/store';
import {
  meLoaded,
  meFailed,
  preferencesSaved,
  preferencesSaveFailed,
  preferencesSaving,
} from '../store/slices/userSlice';
import { themeHydrated } from '../store/slices/themeSlice';
import { fleetLayoutHydrated, ownershipHydrated } from '../store/slices/dronesSlice';
import { userService } from '../services/userService';
import {
  FleetPreferences,
  UserPreferencesPayload,
  normalizePreferences,
} from '../types/user';

/**
 * One setting's worth of change. `fleet` is partial too: it holds two
 * settings, and a caller changing one must not have to restate the other.
 */
export type PreferenceChange = Omit<Partial<UserPreferencesPayload>, 'fleet'> & {
  fleet?: Partial<FleetPreferences>;
};

/** Turns an unknown thrown value into something worth showing a user. */
const messageOf = (error: unknown, fallback: string): string =>
  error instanceof Error && error.message ? error.message : fallback;

/**
 * Loads the signed-in user's account and preferences once per session (#378).
 *
 * Mounted in the authenticated dashboard, so it never fires before sign-in.
 * Hydrating the theme here rather than in the settings screen is deliberate: the
 * preference has to reach the provider whether or not the user ever opens
 * settings, otherwise a theme saved on another device would only appear after
 * visiting the page that saves it.
 */
/**
 * Write preferences without loading them.
 *
 * Split out of useCurrentUser (#528) because that hook *fetches* — calling it
 * from a component only to save a setting makes rendering that component
 * trigger a GET /me. The header theme toggle did exactly that and leaked a
 * pending request into unrelated tests; a presentational control should not
 * cause a network call by existing.
 *
 * Callers that need the profile still use useCurrentUser, which owns the load
 * and re-exports this.
 */
export const usePreferenceWriter = () => {
  const dispatch = useDispatch();
  const { preferences, loaded, saving } = useSelector(
    (state: RootState) => state.user
  );

  // Live values, not the stored document (#528).
  //
  // The stored document is what the server last saw. Anything the operator has
  // changed since — the header theme toggle writes to the theme slice and
  // nowhere else — is absent from it, so a caller spreading `...preferences`
  // saves a stale copy of every setting it is not itself changing.
  //
  // That is exactly what happened: toggling to light and then clicking All /
  // Mine / Unclaimed saved `{...preferences}` with the server's `theme: dark`,
  // and savePreferences re-applied it. The light theme snapped back on the
  // click, and the operator was never told their choice had not been kept.
  const livePreference = useSelector(
    (state: RootState) => state.theme.preference
  );
  const liveOwnership = useSelector(
    (state: RootState) => state.drones.ownershipFilter
  );
  const liveLayout = useSelector(
    (state: RootState) => state.drones.fleetLayout
  );

  const savePreferences = useCallback(
    async (next: UserPreferencesPayload): Promise<boolean> => {
      dispatch(preferencesSaving());
      try {
        const stored = await userService.savePreferences(next);
        const saved = normalizePreferences(stored.payload ?? next);
        dispatch(preferencesSaved(saved));
        dispatch(themeHydrated(saved.theme));
        dispatch(ownershipHydrated(saved.fleet.ownership));
        dispatch(fleetLayoutHydrated(saved.fleet.layout));
        return true;
      } catch (err: unknown) {
        dispatch(
          preferencesSaveFailed(messageOf(err, 'Could not save your preferences'))
        );
        return false;
      }
    },
    [dispatch]
  );

  /**
   * Save one setting without disturbing the others.
   *
   * Composes the document from what is on screen *now*, then applies the
   * change. Prefer this to spreading `preferences` yourself — a partial save
   * built from stale state silently reverts whatever the operator changed most
   * recently.
   */
  const savePreference = useCallback(
    ({ fleet, ...partial }: PreferenceChange): Promise<boolean> =>
      savePreferences({
        ...preferences,
        theme: livePreference,
        ...partial,
        // Merged rather than replaced: the fleet block holds two settings, and
        // saving the layout must not put back an ownership filter from before
        // the last click, or the other way round.
        fleet: { ownership: liveOwnership, layout: liveLayout, ...fleet },
      }),
    [savePreferences, preferences, livePreference, liveOwnership, liveLayout]
  );

  return { preferences, loaded, saving, savePreferences, savePreference };
};

export const useCurrentUser = () => {
  const dispatch = useDispatch();
  // The writing half, shared with components that only save (#528).
  const writer = usePreferenceWriter();
  const { profile, roles, loaded, error } = useSelector(
    (state: RootState) => state.user
  );

  useEffect(() => {
    if (loaded) {
      return;
    }
    let active = true;

    userService
      .getMe()
      .then((me) => {
        if (!active) return;
        dispatch(meLoaded(me));
        const stored = normalizePreferences(me.preferences?.payload);
        // The cached preference already painted the first frame; this confirms
        // it, or corrects it when the user changed the theme elsewhere.
        dispatch(themeHydrated(stored.theme));
        // Declines to overwrite a view the operator has already picked in this
        // session — preferences land a few hundred milliseconds after the first
        // paint, which is long enough to click.
        dispatch(ownershipHydrated(stored.fleet.ownership));
        dispatch(fleetLayoutHydrated(stored.fleet.layout));
      })
      .catch((err: unknown) => {
        if (!active) return;
        // The dashboard stays usable on the cached preference and the defaults.
        // Failing to load settings must not look like a broken fleet view.
        dispatch(meFailed(messageOf(err, 'Could not load your preferences')));
      });

    return () => {
      active = false;
    };
  }, [dispatch, loaded]);

  // Spread last so the writer's view of preferences/loaded/saving wins — they
  // read the same slice, so the values are identical; this just keeps one
  // source rather than two that could drift.
  return { profile, roles, error, ...writer };
};
